import React from 'react';
import { 
  LayoutGrid, 
  Users, 
  Clock, 
  Wallet, 
  MessageSquare, 
  Share2,
  CheckCircle2,
  BarChart3,
  Calendar,
  Target,
  Layers,
  Zap
} from 'lucide-react';

const FEATURES = [
  {
    icon: LayoutGrid,
    title: 'Gestion de projet moderne',
    description: 'Kanban, liste, calendrier, Gantt — choisissez la vue qui vous convient. Gérez tâches, jalons et dépendances sans friction.',
    color: 'indigo',
    highlights: ['Vues multiples', 'Jalons & sprints', 'Sous-tâches illimitées']
  },
  {
    icon: Users,
    title: 'Charge & capacité équipe',
    description: 'Visualisez la charge de chaque membre, détectez les surcharges, répartissez intelligemment le travail.',
    color: 'violet',
    highlights: ['Vue charge temps réel', 'Multi-projets', 'Alertes automatiques']
  },
  {
    icon: Clock,
    title: 'Feuilles de temps intelligentes',
    description: 'Suivi du temps simple et précis. Validation par les managers, historique complet, export facile.',
    color: 'emerald',
    highlights: ['Saisie rapide', 'Workflow validation', 'Rapports détaillés']
  },
  {
    icon: Wallet,
    title: 'Budget projet clair',
    description: 'Prévision vs réel en temps réel. Suivez les coûts main d\'œuvre et dépenses externes sans tableur.',
    color: 'amber',
    highlights: ['Coûts temps réel', 'Alertes budget', 'Analyse rentabilité']
  },
  {
    icon: MessageSquare,
    title: 'Collaboration fluide',
    description: 'Commentaires, mentions, notifications — restez synchronisés sans quitter votre espace de travail.',
    color: 'pink',
    highlights: ['Commentaires tâches', '@mentions', 'Notifications smart']
  },
  {
    icon: Share2,
    title: 'Partage & accès client',
    description: 'Partagez l\'avancement avec vos clients via liens sécurisés. Contrôlez exactement ce qu\'ils voient.',
    color: 'cyan',
    highlights: ['Liens partage', 'Accès lecture', 'Portail client']
  }
];

const colorMap = {
  indigo: {
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    text: 'text-indigo-600 dark:text-indigo-400',
    border: 'border-indigo-200 dark:border-indigo-800'
  },
  violet: {
    bg: 'bg-violet-100 dark:bg-violet-900/30',
    text: 'text-violet-600 dark:text-violet-400',
    border: 'border-violet-200 dark:border-violet-800'
  },
  emerald: {
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    text: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-800'
  },
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800'
  },
  pink: {
    bg: 'bg-pink-100 dark:bg-pink-900/30',
    text: 'text-pink-600 dark:text-pink-400',
    border: 'border-pink-200 dark:border-pink-800'
  },
  cyan: {
    bg: 'bg-cyan-100 dark:bg-cyan-900/30',
    text: 'text-cyan-600 dark:text-cyan-400',
    border: 'border-cyan-200 dark:border-cyan-800'
  }
};

export function FeaturesSection() {
  return (
    <section id="features" className="py-24 md:py-32 bg-white dark:bg-slate-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-sm font-medium mb-4">
            <Layers className="h-4 w-4" />
            Fonctionnalités
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            Tout ce dont vous avez besoin.
            <br />
            <span className="text-slate-500 dark:text-slate-400">Rien de superflu.</span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            Lapyno combine les outils essentiels pour piloter vos projets avec précision, 
            sans la complexité des usines à gaz.
          </p>
        </div>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {FEATURES.map((feature, index) => {
            if (!feature?.icon) return null;
            const Icon = feature.icon;
            const colors = colorMap[feature.color as keyof typeof colorMap];
            
            return (
              <div
                key={feature.title}
                className="group relative p-6 md:p-8 rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 hover:border-indigo-300 dark:hover:border-indigo-700 transition-all duration-300 hover:shadow-xl hover:shadow-indigo-500/5"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Icon */}
                <div className={`inline-flex p-3 rounded-xl ${colors.bg} ${colors.text} mb-5`}>
                  <Icon className="h-6 w-6" />
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-3">
                  {feature.title}
                </h3>

                {/* Description */}
                <p className="text-slate-600 dark:text-slate-400 mb-5 leading-relaxed">
                  {feature.description}
                </p>

                {/* Highlights */}
                <ul className="space-y-2">
                  {feature.highlights.map((highlight) => (
                    <li key={highlight} className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                      <CheckCircle2 className={`h-4 w-4 ${colors.text}`} />
                      {highlight}
                    </li>
                  ))}
                </ul>

                {/* Hover effect line */}
                <div className={`absolute bottom-0 left-0 right-0 h-1 ${colors.bg} rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity`} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
